import { useEffect, useState } from "react";
import Input from "../../../form/Input";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Textarea from "../../../form/Textarea";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import { organizationAddEdit, organizationDetails } from "../../../../services/organizationService";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import FlexibleLanguageSelect from "../../../form/select/FlexibleLanguageSelect";

const validation = {
  name: { required: true, message: "Name is required" },
  contactEmail: [
    {
      required: true,
      message: "Enter Valid Email",
    },
  ],
};

const ManageOrganization = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const { values, handleChange, handleSubmit, errors, setFieldsValue, formValidate } = useForm({
    validationRules: validation,
  });

  const { data: details } = useQuery({
    queryKey: ["organization-details", data?._id],
    queryFn: async () => {
      const res = await organizationDetails({ id: data?._id });
      return res?.data;
    },
    enabled: !!data?._id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        logo: details?.logo?.url,
        coverImage: details?.coverImage?.url,
      });
      setAvailableLanguages(details?.availableLanguages);
      setDefaultLanguage(details?.defaultLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        ...(details?._id && { id: details?._id }),
        availableLanguages: availableLanguages,
        defaultLanguage: defaultLanguage,
      };

      return await organizationAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const languages = [
    { name: "English", value: "en", code: "GB" },
    { name: "Spanish", value: "es", code: "ES" },
    { name: "French", value: "fr", code: "FR" },
    { name: "German", value: "de", code: "DE" },
  ];

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="h-full p-4">
        <div className="grid grid-cols-12 gap-x-4 gap-y-4">
          <div className="relative col-span-6">
            <Input
              label="Organization Name"
              inputPlaceholder={"Enter Organization Name"}
              inputName="name"
              value={values?.name || ""}
              onChange={handleChange}
              {...formValidate(errors, "name")}
            />
          </div>
          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Organization Type"}
              placeholder="Enter Organization Type"
              selectedValue={values?.type}
              dropdownData={[
                { name: "Platform", value: "platform" },
                { name: "Organizer", value: "organizer" },
                { name: "Agency", value: "agency" },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "type", value: val } });
              }}
              {...formValidate(errors, "type")}
            />
          </div>
          <div className="relative col-span-6">
            <FlexibleLanguageSelect
              dropdownData={languages}
              isSearch={true}
              selectedValue={availableLanguages}
              defaultSelected={defaultLanguage}
              type="multiple"
              onChange={(val) => setAvailableLanguages(val)}
              onDefaultChange={(def) => setDefaultLanguage(def)}
            />
          </div>

          <div className="relative col-span-12">
            <FileUploadSingle
              label={"Upload Cover Image"}
              labelClasses={"!mb-2"}
              value={values?.coverImage}
              onChange={(file) => handleChange({ target: { name: "coverImage", value: file } })}
              {...formValidate(errors, "coverImage")}
            />
          </div>

          <div className="relative col-span-12">
            <FileUploadSingle
              label={"Upload Logo Image"}
              labelClasses={"!mb-2"}
              value={values?.logo}
              onChange={(file) => handleChange({ target: { name: "logo", value: file } })}
              {...formValidate(errors, "logo")}
            />
          </div>

          <div className="relative col-span-12">
            <Textarea
              label="Description"
              inputPlaceholder={"Enter Description"}
              inputName="description"
              inputValue={values?.description || ""}
              onChange={handleChange}
              {...formValidate(errors, "description")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Contact Email"
              inputPlaceholder={"Enter Contact Email"}
              inputName="contactEmail"
              value={values?.contactEmail || ""}
              onChange={handleChange}
              {...formValidate(errors, "contactEmail")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Website"
              inputPlaceholder={"Enter Website"}
              inputName="website"
              value={values?.website || ""}
              onChange={handleChange}
              {...formValidate(errors, "website")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Verified"}
              placeholder="Select Verified"
              selectedValue={values?.verified}
              dropdownData={[
                { name: "Yes", value: true },
                { name: "No", value: false },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "verified", value: val } });
              }}
              {...formValidate(errors, "verified")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Suspended"}
              placeholder="Select Suspended"
              selectedValue={values?.isSuspended}
              dropdownData={[
                { name: "Yes", value: true },
                { name: "No", value: false },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "isSuspended", value: val } });
              }}
              {...formValidate(errors, "isSuspended")}
            />
          </div>
        </div>
        <div className="pt-3 pb-6 flex justify-end mt-5">
          <Button
            buttonType="submit"
            buttonClasses={"min-w-36"}
            buttonLabel={data?._id ? "Update Organization" : "Save Organization"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageOrganization;
